import React from "react";
import { FinancialRecord } from "@akfdev-com/shared";
import { toGbpDisplay } from "./gbp";
import { computeSummary } from "./summary";
import type { SummaryPeriod } from "./types";

interface FinancialSummaryPanelProps {
  records: FinancialRecord[];
  period: SummaryPeriod;
}

/**
 * Financial Summary Panel
 *
 * Renders aggregate financial figures for a contact:
 * - Total Invoiced, Paid, Outstanding
 * - Total Owed (to us, green), Owing (by us, red)
 * - Last Invoice and Last Payment dates
 *
 * All amounts computed from the same owner + contact-scoped record set within the active period.
 * Reconciliation: Total Outstanding === Total Invoiced - Total Paid (exact integer pence).
 *
 * Validates: Requirements 7.2, 7.5, 8.5
 */
export const FinancialSummaryPanel: React.FC<FinancialSummaryPanelProps> = ({
  records,
  period,
}) => {
  const now = new Date();
  const summary = computeSummary(records, period, now);

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">
          Billing & Financial Summary
        </h2>
      </div>
      <div className="divide-y divide-gray-100">
        {/* Period label */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Period</span>
          <span className="text-sm font-medium text-gray-900">
            {summary.periodLabel === "FY"
              ? `FY ${new Date(now.getFullYear(), 3).getFullYear()}/${String(
                  new Date(now.getFullYear(), 3).getFullYear() + 1,
                ).slice(-2)}`
              : "All-time"}
          </span>
        </div>

        {/* Total Invoiced */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Total Invoiced</span>
          <span className="text-sm font-semibold text-gray-900">
            £{toGbpDisplay(summary.totalInvoicedPence)}
          </span>
        </div>

        {/* Total Paid */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Total Paid</span>
          <span className="text-sm font-semibold text-gray-900">
            £{toGbpDisplay(summary.totalPaidPence)}
          </span>
        </div>

        {/* Total Outstanding */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Total Outstanding</span>
          <span className="text-sm font-semibold text-gray-900">
            £{toGbpDisplay(summary.totalOutstandingPence)}
          </span>
        </div>

        {/* Total Owed (to us, green) */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Total Owed (to us)</span>
          <span className="text-sm font-semibold" style={{ color: "#16a34a" }}>
            £{toGbpDisplay(summary.totalOwedPence)}
          </span>
        </div>

        {/* Total Owing (by us, red) */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Total Owing (by us)</span>
          <span className="text-sm font-semibold" style={{ color: "#dc2626" }}>
            £{toGbpDisplay(summary.totalOwingPence)}
          </span>
        </div>

        {/* Last Invoice */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Last Invoice</span>
          <span className="text-sm text-gray-900">
            {summary.lastInvoiceDate
              ? new Date(summary.lastInvoiceDate).toLocaleDateString("en-GB")
              : "—"}
          </span>
        </div>

        {/* Last Payment */}
        <div className="px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">Last Payment</span>
          <span className="text-sm text-gray-900">
            {summary.lastPaymentDate
              ? new Date(summary.lastPaymentDate).toLocaleDateString("en-GB")
              : "—"}
          </span>
        </div>
      </div>
    </div>
  );
};
